import { Button, Form, Input } from 'antd';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNotify } from '@/hooks/useNotify';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useMutationReset } from '@/hooks/useMutationReset';
import { useUpdateProfileMutation } from '@/modules/user/userApi';
import { updateUser } from '@/store/authSlice';
import { useAppDispatch } from '@/store/hooks';
import type { UpdateProfilePayload, User } from '@/types/auth';
import { resolveMutationResult } from '@/utils/apiResponse';
import { emailFieldRules } from '@/utils/validation';
import styles from '../Account.module.scss';

interface ProfileTabContentProps {
  user: User | null | undefined;
}

export function ProfileTabContent({ user }: ProfileTabContentProps) {
  const dispatch = useAppDispatch();
  const notify = useNotify();
  const [updateProfile, { isLoading, reset }] = useUpdateProfileMutation();
  useGlobalLoader(isLoading);
  const { control, handleSubmit, reset: resetForm } = useForm<UpdateProfilePayload>({
    defaultValues: { firstName: '', lastName: '', email: '' },
  });

  useMutationReset(reset);

  useEffect(() => {
    if (user) {
      resetForm({
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        email: user.email ?? '',
      });
    }
  }, [user, resetForm]);

  const onSubmit = async (values: UpdateProfilePayload) => {
    const result = await resolveMutationResult<User>(updateProfile(values));

    if (result.ok) {
      dispatch(updateUser(result.data));
      notify.success(result.message ?? 'Profile updated successfully.');
      return;
    }

    notify.error(result.message);
  };

  return (
    <div className={styles.tabContent}>
      <Form layout="vertical" className={styles.profileForm} onFinish={handleSubmit(onSubmit)}>
        <div className={styles.formGrid}>
          <Controller
            name="firstName"
            control={control}
            rules={{ required: 'First name is required' }}
            render={({ field, fieldState }) => (
              <Form.Item
                label="First name"
                required
                validateStatus={fieldState.error ? 'error' : ''}
                help={fieldState.error?.message}
              >
                <Input {...field} placeholder="First name" />
              </Form.Item>
            )}
          />

          <Controller
            name="lastName"
            control={control}
            rules={{ required: 'Last name is required' }}
            render={({ field, fieldState }) => (
              <Form.Item
                label="Last name"
                required
                validateStatus={fieldState.error ? 'error' : ''}
                help={fieldState.error?.message}
              >
                <Input {...field} placeholder="Last name" />
              </Form.Item>
            )}
          />
        </div>

        <Controller
          name="email"
          control={control}
          rules={emailFieldRules}
          render={({ field, fieldState }) => (
            <Form.Item
              label="Email"
              required
              validateStatus={fieldState.error ? 'error' : ''}
              help={fieldState.error?.message}
            >
              <Input {...field} type="email" placeholder="you@example.com" />
            </Form.Item>
          )}
        />

        <div className={styles.formActions}>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            Save changes
          </Button>
        </div>
      </Form>
    </div>
  );
}
